
import { useState, useEffect, useCallback } from 'react';
import { EventFilters, EventType } from '@/lib/types';
import { DEFAULT_FILTERS } from '@/lib/eventFilters';
import { useAuth } from '@/contexts/auth/AuthContext';

// Létrehozunk egy állandót a localStorage kulcsának
const FILTER_STORAGE_KEY = 'medihub-filter-settings';

export const useFilterBar = (onFilterChange: (filters: EventFilters) => void) => {
  const loadSavedFilters = (): {
    search: string;
    location: string;
    selectedTypes: Record<EventType, boolean>;
    selectedProfessions: Record<string, boolean>;
    hasCreditPoints: boolean;
    dateRange: {
      startDate: string;
      endDate: string;
    };
  } => {
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error('Hiba a mentett szűrők betöltésekor:', error);
    }
    
    // Ha nincs mentett szűrő vagy hiba történt, visszaadjuk az alapértelmezett értékeket
    return {
      search: '',
      location: '',
      selectedTypes: {
        conference: false,
        workshop: false,
        training: false,
        webinar: false,
        meetup: false,
        seminar: false,
        networking: false,
        exhibition: false,
        congress: false,
        course: false,
        symposium: false
      },
      selectedProfessions: {},
      hasCreditPoints: false,
      dateRange: {
        startDate: '',
        endDate: '',
      }
    };
  };

  // Inicializálás a mentett értékekkel
  const savedFilters = loadSavedFilters();
  const [search, setSearch] = useState(savedFilters.search);
  const [location, setLocation] = useState(savedFilters.location);
  const [selectedTypes, setSelectedTypes] = useState<Record<EventType, boolean>>(savedFilters.selectedTypes);
  const [selectedProfessions, setSelectedProfessions] = useState<Record<string, boolean>>(savedFilters.selectedProfessions);
  const [hasCreditPoints, setHasCreditPoints] = useState(savedFilters.hasCreditPoints);
  const [dateRange, setDateRange] = useState(savedFilters.dateRange);

  const { user } = useAuth();

  // Determine if any filters are active
  const hasActiveFilters = useCallback(() => {
    return (
      search !== '' ||
      location !== '' ||
      Object.values(selectedTypes).some(Boolean) ||
      Object.values(selectedProfessions).some(Boolean) ||
      hasCreditPoints ||
      dateRange.startDate !== '' ||
      dateRange.endDate !== ''
    );
  }, [search, location, selectedTypes, selectedProfessions, hasCreditPoints, dateRange]);

  const handleTypeClick = (eventType: EventType) => {
    setSelectedTypes(prev => ({
      ...prev,
      [eventType]: !prev[eventType]
    }));
  };

  const handleProfessionClick = (professionId: string) => {
    setSelectedProfessions(prev => ({
      ...prev,
      [professionId]: !prev[professionId]
    }));
  };

  const toggleCreditPointsFilter = () => {
    setHasCreditPoints(prev => !prev);
  };

  const resetFilters = () => {
    setSearch('');
    setLocation('');
    setSelectedTypes({
      conference: false,
      workshop: false,
      training: false,
      webinar: false,
      meetup: false,
      seminar: false,
      networking: false,
      exhibition: false,
      congress: false,
      course: false,
      symposium: false
    });
    setSelectedProfessions({});
    setHasCreditPoints(false);
    setDateRange({
      startDate: '',
      endDate: '',
    });
    
    // Töröljük a mentett szűrőket
    try {
      localStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (error) {
      console.error('Hiba a mentett szűrők törlésekor:', error);
    }
  };

  // Apply user preferences as filters
  const applyPreferenceFilter = () => {
    // Get preferred topics safely with optional chaining
    const preferredTopics = user?.userProfile.notificationPreferences?.preferredTopics || [];

    if (preferredTopics.length > 0) {
      // Reset current filters
      resetFilters();

      // Apply preferences as filters
      const newProfessions: Record<string, boolean> = {};
      preferredTopics.forEach(topic => {
        newProfessions[topic] = true;
      });

      setSelectedProfessions(newProfessions);
    }
  };

  // Update filters whenever any dependency changes
  useEffect(() => {
    // Collect all active types
    const activeTypes: EventType[] = [];
    Object.entries(selectedTypes).forEach(([type, isSelected]) => {
      if (isSelected) {
        activeTypes.push(type as EventType);
      }
    });

    // Collect all active professions
    const activeProfessions: string[] = [];
    Object.entries(selectedProfessions).forEach(([profession, isSelected]) => {
      if (isSelected) {
        activeProfessions.push(profession);
      }
    });

    // Build the filters object
    const filters: EventFilters = {
      ...DEFAULT_FILTERS,
      search,
      location,
      type: activeTypes.length ? activeTypes : DEFAULT_FILTERS.type,
      professions: activeProfessions.length ? activeProfessions : DEFAULT_FILTERS.professions,
      hasCreditPoints,
      dateRange: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }
    };

    onFilterChange(filters);
    
    // Mentsük a jelenlegi szűrőket a localStorage-ba
    try {
      const filtersToSave = {
        search,
        location,
        selectedTypes,
        selectedProfessions,
        hasCreditPoints,
        dateRange
      };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (error) {
      console.error('Hiba a szűrők mentésekor:', error);
    }
  }, [search, location, selectedTypes, selectedProfessions, hasCreditPoints, dateRange, onFilterChange]);

  return {
    search,
    setSearch,
    location,
    setLocation,
    selectedTypes,
    selectedProfessions,
    hasCreditPoints,
    dateRange,
    setDateRange,
    handleTypeClick,
    handleProfessionClick,
    toggleCreditPointsFilter,
    resetFilters,
    applyPreferenceFilter,
    hasActiveFilters: hasActiveFilters()
  };
};
